<template>
  <FusionBaseDialog
    :open="open"
    :title="$t('map.deviceAlerts.setUpTitle')"
    persistent-overlay
    padded
    max-height="90vh"
    @close="close"
    @after-leave="afterLeave"
    content-class="device-alerts-dialog"
    data-cy="deviceAlertsDialog"
  >
    <!-- Alerts view headers -->
    <template v-slot:header>
      <DeviceAlertsHeader v-model="currentAlert" :title="title" :icon="icon" :dataCy="dataCy" />
    </template>
    <!-- delete message -->
    <div v-if="deleteViewOpen">
      <p class="mx-4">{{ $t('map.deviceAlerts.delete.message') }}</p>
    </div>
    <!-- progress spinner -->
    <div
      v-else-if="!updateViewOpen && alerts === null && !isEnableNotification"
      class="text-center emptyMessage"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        data-cy="alertLoadingSpinner"
      ></v-progress-circular>
    </div>
    <!-- empty message -->
    <div
      v-else-if="!updateViewOpen && alerts.length === 0 && !isEnableNotification"
      class="text-center emptyMessage"
      data-cy="emptyAlertMessage"
    >
      {{ $t('map.deviceAlerts.empty') }}
    </div>
    <v-list
      v-else-if="!updateViewOpen && alerts.length > 0 && !isEnableNotification"
      class="alerts-list"
    >
      <v-list-item
        v-for="(alert, index) in alerts"
        :key="index"
        class="alerts-list-item"
        :class="{ 'pb-0': xs }"
      >
        <v-divider v-if="index > 0" class="mb-4" />
        <div class="alerts-list-item-actions">
          <v-list-item-title data-cy="listTitle">
            {{ alert.title }}
          </v-list-item-title>
          <v-spacer />
          <template v-if="!xs">
            <v-btn
              @click="startAlertEdit(alert)"
              variant="text"
              prepend-icon="mdi-pencil"
              data-cy="editIcon"
            >
              {{ $t('buttons.edit') }}
            </v-btn>
            <v-btn
              @click="confirmDeleteAlert(alert.id)"
              variant="text"
              prepend-icon="mdi-delete"
              data-cy="deleteIcon"
            >
              {{ $t('buttons.delete') }}
            </v-btn>
          </template>
        </div>
        <div class="alerts-list-item-text">
          <v-list-item-subtitle>
            <span data-cy="listCreationDate"
              >{{ $t('map.deviceAlerts.fields.creationDate') }}:
            </span>
            <span data-cy="listCreationDateData">
              {{ new Date(alert.createdAt).toLocaleString() }}</span
            >
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            <span data-cy="listAlertEnabled">{{ $t('map.deviceAlerts.fields.enabled') }}: </span>
            <span data-cy="listAlertEnabledData">
              {{ alert.enabled ? $t('boolean.yes') : $t('boolean.no') }}
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle data-cy="listAlertLevel">
            <span>{{ $t('map.deviceAlerts.fields.level') }}: </span>
            <span>{{ $t(`map.deviceAlerts.fields.levels.${alert.severity}`) }}</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="isBlueSkyOrAirAssure">
            <span data-cy="listAlertSensor">{{ $t('map.deviceAlerts.fields.sensor') }}: </span>
            <span v-if="alert.condition.field">
              {{ $t(`map.deviceAlerts.fields.sensors.${alert.condition.field}`) }}
            </span>
            <span v-else>
              {{ $t('map.deviceAlerts.fields.sensors.none') }}
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle data-cy="listAlertCondition">
            <span v-if="!isBlueSkyOrAirAssure">
              <span>{{ $t('map.deviceAlerts.fields.condition') }}: </span>
              <span>{{
                $t(
                  `map.deviceAlerts.fields.${modelProductFamily}Conditions.${alert.condition.field}`
                )
              }}</span>
            </span>
            <span v-else>
              <span>{{ $t('map.deviceAlerts.fields.condition') }}: </span>
              <span>{{
                $t(`map.deviceAlerts.fields.conditions.${alert.condition.operator}`, {
                  value: alert.condition.value
                })
              }}</span>
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="alert.condition.pmChannel" data-cy="listPMChannel">
            <span>{{ $t('map.deviceAlerts.fields.pmChannel') }}: </span>
            <span>{{
              $t(
                `map.deviceAlerts.fields.${modelProductFamily}Conditions.pmChannels.${alert.condition.pmChannel}`
              )
            }}</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle data-cy="listFieldDeliverTo">
            <span>{{ $t('map.deviceAlerts.fields.deliverTo') }}: </span>
            <span v-if="alert.sendSms && phone && phone.number">
              <span>{{ $t('map.deviceAlerts.fields.mobile') }}</span>
              <span v-if="smAndUp"> ({{ phone.number }}) </span>
            </span>
            <span v-if="alert.sendSms && alert.sendEmail">, </span>
            <span v-if="alert.sendEmail">
              <span>{{ $t('map.deviceAlerts.fields.email') }}</span>
              <span v-if="smAndUp"> ({{ userData?.email }}) </span>
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle data-cy="listFieldMessage">
            <span>{{ $t('map.deviceAlerts.fields.message') }}: </span>
            <span>{{ alert.userMessage }}</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            <span>{{ $t('map.deviceAlerts.fields.frequency') }}: </span>
            <span>{{ getFrequency(alert.frequencyType) }}</span>
          </v-list-item-subtitle>
        </div>
        <div v-if="xs" class="alerts-list-item-actions">
          <v-spacer />
          <v-btn
            @click="startAlertEdit(alert)"
            variant="text"
            prepend-icon="mdi-pencil"
            data-cy="editIcon"
          >
            {{ $t('buttons.edit') }}
          </v-btn>
          <v-btn
            @click="confirmDeleteAlert(alert.id)"
            variant="text"
            prepend-icon="mdi-delete"
            data-cy="deleteIcon"
          >
            {{ $t('buttons.delete') }}
          </v-btn>
        </div>
      </v-list-item>
    </v-list>
    <!-- Add/edit view -->
    <UpdateAlert
      v-else-if="updateViewOpen"
      v-model="currentAlert"
      :rules="rules"
      :isNotificationEnabled="isNotificationBtnEnabled"
      :isEditingAlert="editingAlert"
      :isSmsDisabled="isSmsDisabled"
      :isEmailDisabled="isEmailDisabled"
      :phone="phone"
      :deviceId="deviceId"
      :measurements="sensors"
      :helpText="helpText"
      :modelProductFamily="modelProductFamily"
      :alertables="alertables"
      @toggleNotification="toggleNotification"
      @onDisclaimerAgree="toggleNotificationsTitle"
    />

    <template v-slot:actions>
      <!-- Delete actions -->
      <template v-if="deleteViewOpen">
        <v-btn
          @click="cancelDeleteAlert()"
          variant="outlined"
          class="text-black"
          data-cy="deleteCancelButton"
        >
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          @click="deleteAlert()"
          :class="{ disabled: alerts === null }"
          :disabled="alerts === null"
          class="bg-primary text-white"
          variant="text"
          data-cy="deleteAlertButton"
        >
          {{ $t('buttons.delete') }}
        </v-btn>
      </template>
      <!-- Default actions -->
      <template v-else-if="!updateViewOpen && !isEnableNotification">
        <v-tooltip location="bottom" :disabled="hasRemainingAlerts">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              @click="startAlertAdd"
              variant="outlined"
              class="text-black"
              data-cy="addButton"
              :class="[{ restrictedContent: !hasRemainingAlerts }]"
            >
              {{ $t('buttons.add') }}
            </v-btn>
          </template>
          <span>
            {{ $t('map.deviceAlerts.upgradeTooltip') }}
          </span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <v-btn @click="close" class="bg-primary text-white" variant="text" data-cy="doneButton">
          {{ $t('buttons.done') }}
        </v-btn>
      </template>
      <!-- update actions -->
      <template v-else-if="updateViewOpen && !isEnableNotification">
        <v-btn
          @click="closeUpdateView()"
          variant="outlined"
          class="text-black"
          data-cy="cancelAddButton"
        >
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          @click="updateAlert"
          class="bg-primary text-white"
          :disabled="!fieldsValid"
          variant="text"
          data-cy="saveButton"
        >
          {{ $t('buttons.save') }}
        </v-btn>
      </template>
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, toRaw } from 'vue'
import { useDisplay } from 'vuetify'
import { getAuthInstance } from '@fusion/auth-v3'
import { FusionStores } from '@fusion/saas-components-v3'
import { HTTPStatus } from '@/api'
import UpdateAlert from '../alerts/UpdateAlert.vue'
import DeviceAlertsHeader from '../alerts/DeviceAlertsHeader.vue'
import { ProductFamily } from '@/services/map'
import { prefixToNewField } from '@/services/device-alerts'
import type {
  Trigger,
  Severity,
  FrequencyType,
  Operator,
  ConditionValue,
  Condition,
  UpdateTrigger
} from '@/types/alerts'
import type { DeviceModelField } from '@/types/devices'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { UnpackedTelemetryField } from '@/services/device-service/devices.ts'

const ValidNum = /^-?(0|[1-9]\d*)(\.\d+)?$/

const titles = {
  delete: 'deleteTitle',
  notifications: 'notificationsTitle',
  enableNotification: 'enableNotificationTitle',
  add: 'addTitle',
  edit: 'editTitle',
  setUp: 'setUpTitle'
}

const cyHeaders = {
  delete: 'deleteAlertHeader',
  notifications: 'notificationsHeader',
  enableNotification: 'enableNotificationHeader',
  add: 'addAlertHeader',
  edit: 'editAlertHeader',
  setUp: 'alertSetupHeader'
}

interface PmChannel {
  pmChannel?: string
}

type ConditionDraft = Condition & PmChannel

export interface TriggerDraft {
  title: string
  userMessage: string
  enabled: boolean
  severity: Severity
  condition: ConditionDraft
  frequencyType: FrequencyType
  sendSms: boolean
  sendEmail: boolean
  id?: string
}

export interface Phone {
  id: string
  isPrimary: boolean
  number: string
  allowSmsNotifications: boolean
  smsNotificationsPending: boolean
}

export interface FormattedTrigger extends Omit<Trigger, 'condition'> {
  condition?: ConditionDraft
}

export interface SelectItem {
  title: string
  value: number | string
}

export interface RangeLimit {
  min: number
  max: number | null
}

export interface DeviceInfo {
  id: string | undefined
  model: string
  submodel: string
  isShared: boolean
  sensorMetaData: UnpackedTelemetryField
}

const DEFAULT_ALERT: TriggerDraft = {
  title: '',
  userMessage: '',
  enabled: true,
  severity: 'standard' as Severity,
  condition: {
    field: null,
    type: 'number',
    operator: 'gte' as Operator,
    value: '' as ConditionValue,
    pmChannel: null
  } as ConditionDraft,
  frequencyType: 'onceCrossed',
  sendSms: false,
  sendEmail: false
}

export default defineComponent({
  name: 'DeviceAlertsDialog',
  setup() {
    const { xs, smAndUp } = useDisplay()
    const userProfileStore = FusionStores.userProfile(getAuthInstance())
    return { xs, smAndUp, userProfileStore }
  },
  props: {
    open: Boolean,
    deviceId: String
  },
  emits: ['close', 'afterLeave'],
  components: {
    UpdateAlert,
    DeviceAlertsHeader
  },
  data() {
    return {
      deleteViewOpen: false,
      updateViewOpen: false,
      addingAlert: false,
      editingAlert: false,
      currentAlert: { ...DEFAULT_ALERT },
      alerts: null as FormattedTrigger[] | null,
      alertSelectedForDeletion: null as string | null,
      isNotificationBtnEnabled: false,
      isEnableNotification: false,
      phone: null as Phone | null,
      userDetails: {} as Record<string, unknown>,
      isSmsDisabled: false,
      isEmailDisabled: false,
      isNotificationsTitle: false,
      productFamilies: ProductFamily
    }
  },
  async mounted(): Promise<void> {
    await this.getPrimaryPhone()
    this.setAlerts()
  },
  watch: {
    open(): void {
      this.setAlerts()
    },
    userData(user: { allowEmailNotifications?: boolean } | null): void {
      if (user) {
        this.handleDeliveryMethods()
      }
    }
  },
  computed: {
    fieldsValid(): boolean {
      if (
        this.rules.required(this.currentAlert.title) !== true ||
        this.rules.required(this.currentAlert.condition.value) !== true ||
        this.rules.threshold(this.currentAlert.condition.value) !== true ||
        this.rules.precision(this.currentAlert.condition.value) !== true ||
        this.rules.validNum(this.currentAlert.condition.value) !== true ||
        this.rules.required(this.currentAlert.condition.field) !== true
      ) {
        return false
      }
      return true
    },
    hasRemainingAlerts(): boolean {
      if (this.alerts) {
        return this.alerts.length < Number.MAX_SAFE_INTEGER
      }
      return false
    },
    allowedRangeLimits(): Record<string, RangeLimit | undefined> {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_ALLOWED_RANGE_LIMITS}`
      ](this.device.model, this.device.submodel)
    },
    calibrationLimits(): Record<string, { offset: RangeLimit } | undefined> {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_CALIBRATION_LIMITS}`
      ](this.device.model, this.device.submodel)
    },
    allowedRangePlusOffset(): Record<string, RangeLimit> {
      const ranges: Record<string, RangeLimit> = {}
      this.sensors.forEach((measurement) => {
        if (!measurement) return
        // starting range for any measurement is min = 0 with no max
        let range: RangeLimit = { min: 0, max: null }
        const name = this.device.sensorMetaData[measurement.value].shortName

        // apply allowed range limits for measurement
        const limits = this.allowedRangeLimits[name]
        if (limits) {
          range = { ...range, ...limits }
        }

        // apply allowed user calibration offset limits to range
        const offset = this.calibrationLimits[name]?.offset
        if (offset) {
          range.min += offset.min
          if (range.max !== null) {
            range.max += offset.max
          }
        }

        ranges[name] = range
      })
      return ranges
    },
    rules(): {
      required: (value: ConditionValue) => boolean | string
      threshold: (value: ConditionValue) => boolean | string
      validNum: (value: ConditionValue) => boolean | string
      precision: (value: ConditionValue) => boolean | string
    } {
      return {
        required: (value: ConditionValue) =>
          !!value || value === 0 || (this.$t('validation.required') as string),
        threshold: (value: ConditionValue) => {
          const { operator, field } = this.currentAlert.condition
          const invalidEntry = this.$t('validation.invalidEntry') as string
          // DT/SB alerts only have state alerts at the moment, so don't need to validate unless AA/BS
          if (field && this.isBlueSkyOrAirAssure) {
            const floatVal = parseFloat(String(value))
            const { shortName } = this.device.sensorMetaData[field]
            const { min, max } = this.allowedRangePlusOffset[shortName]

            if (!this.valueInRange(floatVal, min, max)) {
              return invalidEntry
            }
            if (!this.validWithOperator(floatVal, min, max, operator)) {
              return invalidEntry
            }
          }
          return true
        },
        validNum: (value: ConditionValue) => {
          if (typeof value === 'boolean') {
            return true
          }
          return ValidNum.test(String(value)) || (this.$t('validation.invalidEntry') as string)
        },
        precision: (value: ConditionValue) => {
          const { field } = this.currentAlert.condition
          // DT/SB alerts only have state alerts at the moment, so don't need to validate unless AA/BS
          if (this.isBlueSkyOrAirAssure) {
            const fieldDetails = this.device.sensorMetaData[field]
            const valueArray = String(value).split('.')
            if (valueArray.length > 1) {
              return (
                valueArray[1].length <= fieldDetails.precision ||
                (this.$t('validation.invalidEntry') as string)
              )
            }
          }
          return true
        }
      }
    },
    helpText(): string {
      let message = ''
      const { field } = this.currentAlert.condition

      if (field && this.isBlueSkyOrAirAssure) {
        const { precision, shortName } = this.device.sensorMetaData[field]
        const { min, max } = this.allowedRangePlusOffset[shortName]
        const measurementName = this.$t(`map.deviceAlerts.fields.sensors.${field}`) as string
        const decimalPlacesText = this.$t(
          'map.deviceSettings.calibrationFactors.decimalPlaces',
          precision
        ) as string

        if (max === null) {
          message = this.$t('map.deviceAlerts.fields.helpTextNoMax', {
            measurement: measurementName,
            rangeLow: min,
            decimalPlaces: decimalPlacesText
          }) as string
        } else {
          message = this.$t('map.deviceAlerts.fields.helpText', {
            measurement: measurementName,
            rangeLow: min,
            rangeHigh: max,
            decimalPlaces: decimalPlacesText
          }) as string
        }
        const unitNoteKey = 'map.deviceAlerts.fields.helpTextUnitNotes.' + shortName
        if (this.$te(unitNoteKey)) {
          message = `${message} ${this.$t(unitNoteKey)}`
        }
      }

      return message
    },
    device(): DeviceInfo {
      const model = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ](this.deviceId)
      return {
        id: this.deviceId,
        model,
        submodel: this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
        ](this.deviceId),
        isShared: this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
        ](this.deviceId),
        sensorMetaData:
          this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_TELEMETRY_FIELDS_BY_MODEL}`
          ](model)
      }
    },
    sensors(): Array<SelectItem | undefined> {
      const alertableSensors: string[] = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_ALERTABLE_MEASUREMENTS}`
      ](this.device.model, this.device.submodel)
      return alertableSensors.map((measurement) => {
        const sensorMetaData = this.device.sensorMetaData[measurement]
        if (sensorMetaData) {
          return {
            value: measurement,
            title: this.$t(`map.deviceAlerts.fields.sensors.${measurement}`) as string
          }
        }
        return undefined
      })
    },
    userData() {
      return this.userProfileStore.user
    },
    modelProductFamily(): ProductFamily {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_PRODUCT_FAMILY}`
      ](this.device.model)
    },
    alertables(): DeviceModelField[] {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_STATE_FIELDS}`
      ](this.device.model)
    },
    title(): string {
      if (this.deleteViewOpen) return titles.delete
      if (this.isNotificationsTitle) return titles.notifications
      if (this.isEnableNotification) return titles.enableNotification
      if (this.updateViewOpen && this.addingAlert) return titles.add
      if (this.updateViewOpen && this.editingAlert) return titles.edit
      return titles.setUp
    },
    icon(): string {
      if (this.deleteViewOpen) return 'mdi-delete'
      if (
        (this.updateViewOpen && this.addingAlert) ||
        this.isEnableNotification ||
        this.isNotificationsTitle
      ) {
        return 'mdi-bell-plus'
      }
      if (this.updateViewOpen && this.editingAlert) return 'mdi-pencil'
      return 'mdi-bell'
    },
    dataCy(): string {
      if (this.deleteViewOpen) return cyHeaders.delete
      if (this.isNotificationsTitle) return cyHeaders.notifications
      if (this.isEnableNotification) return cyHeaders.enableNotification
      if (this.updateViewOpen && this.addingAlert) return cyHeaders.add
      if (this.updateViewOpen && this.editingAlert) return cyHeaders.edit
      return cyHeaders.setUp
    },
    isBlueSkyOrAirAssure(): boolean {
      return (
        this.modelProductFamily === ProductFamily.BLUESKY ||
        this.modelProductFamily === ProductFamily.AIRASSURE
      )
    }
  },
  methods: {
    valueInRange(value: number, min: number, max: number | null): boolean {
      return max !== null ? value >= min && value <= max : value >= min
    },
    validWithOperator(value: number, min: number, max: number | null, operator: Operator): boolean {
      let isValid = true
      if (operator === 'lt') isValid = value > min
      if (operator === 'lte') isValid = value >= min
      if (operator === 'gt' && max !== null) isValid = value < max
      if (operator === 'gte' && max !== null) isValid = value <= max
      return isValid
    },
    async handleDeliveryMethods(): Promise<void> {
      await this.getPrimaryPhone()
      this.isNotificationBtnEnabled = !(
        this.phone?.allowSmsNotifications ||
        this.phone?.smsNotificationsPending ||
        this.userData?.allowEmailNotifications
      )
      this.isSmsDisabled = !(
        this.phone?.allowSmsNotifications || this.phone?.smsNotificationsPending
      )
      this.isEmailDisabled = !this.userData?.allowEmailNotifications
    },
    close(): void {
      this.$emit('close')
    },
    afterLeave(): void {
      this.closeUpdateView()
      this.alerts = null
      this.$emit('afterLeave')
    },
    closeUpdateView(): void {
      this.updateViewOpen = false
      this.addingAlert = false
      this.editingAlert = false
      this.isNotificationBtnEnabled = false
      this.isEnableNotification = false
      this.isNotificationsTitle = false
      this.clearCurrentAlert()
      this.setAlerts()
    },
    getFormattedAlerts(alerts: Trigger[] | null): FormattedTrigger[] | null {
      if (alerts?.length) {
        return alerts.map((alert) => {
          const field = alert.condition?.field || ''
          for (const [prefix, newFieldName] of Object.entries(prefixToNewField)) {
            if (field.includes(prefix)) {
              return {
                ...alert,
                condition: {
                  ...alert.condition,
                  field: newFieldName,
                  pmChannel: field.replace(prefix, '')
                }
              }
            }
          }
          return alert
        })
      }
      return alerts
    },
    async setAlerts(): Promise<void> {
      if (!this.open) return

      try {
        const res = await this.$api.getAlerts(this.deviceId)
        if (!res.ok) {
          this.alerts = []
          return
        }

        const alerts = await res.json()
        this.alerts = this.getFormattedAlerts(alerts)
      } catch {
        this.alerts = []
      }
    },
    setPMChannelCondition(): void {
      if (this.currentAlert.condition.pmChannel) {
        for (const [prefix, newFieldName] of Object.entries(prefixToNewField)) {
          if (this.currentAlert.condition.field === newFieldName) {
            this.currentAlert.condition.field = `${prefix}${this.currentAlert.condition.pmChannel}`
            break
          }
        }
      }
    },
    async updateAlert(): Promise<void> {
      this.setPMChannelCondition()
      if (this.addingAlert) await this.addAlert()
      if (this.editingAlert) await this.editAlert()
      this.setAlerts()
    },
    async addAlert(): Promise<void> {
      const newAlert = this.currentAlert
      const parsedValue = parseFloat(String(this.currentAlert.condition.value))
      if (!Number.isNaN(parsedValue)) {
        this.currentAlert.condition.value = parsedValue
      }
      newAlert.condition = this.currentAlert.condition
      this.alerts = null
      this.updateViewOpen = false
      this.addingAlert = false

      try {
        await this.$api.postAlert(newAlert, this.deviceId)
      } catch {
        // Preserve existing behavior: reload and clear form even on failure.
      } finally {
        this.setAlerts()
        this.clearCurrentAlert()
      }
    },
    async deleteAlert(): Promise<void> {
      if (this.alertSelectedForDeletion) {
        this.alerts = null

        await this.$api.deleteAlert(this.deviceId, this.alertSelectedForDeletion)
        this.deleteViewOpen = false
        this.setAlerts()
      }
    },
    async editAlert(): Promise<void> {
      const parsedValue = parseFloat(String(this.currentAlert.condition.value))
      const conditionValue: ConditionValue = !Number.isNaN(parsedValue)
        ? parsedValue
        : this.currentAlert.condition.value

      const uAlert: UpdateTrigger = {
        condition: { value: conditionValue },
        enabled: this.currentAlert.enabled,
        severity: this.currentAlert.severity,
        userMessage: this.currentAlert.userMessage,
        sendSms: this.currentAlert.sendSms,
        sendEmail: this.currentAlert.sendEmail,
        frequencyType: this.currentAlert.frequencyType
      }
      const uId = this.currentAlert.id

      this.updateViewOpen = false
      this.editingAlert = false

      try {
        await this.$api.patchAlert(uAlert, this.deviceId, uId)
      } catch {
        // Preserve existing behavior: reload and clear form even on failure.
      } finally {
        this.setAlerts()
        this.clearCurrentAlert()
      }
    },
    startAlertAdd(): void {
      if (this.hasRemainingAlerts) {
        this.updateViewOpen = true
        this.addingAlert = true
        this.handleDeliveryMethods()
      }
    },
    startAlertEdit(alert: Trigger): void {
      this.currentAlert = {
        ...alert,
        condition: { ...alert.condition, pmChannel: '' }
      } as TriggerDraft
      this.updateViewOpen = true
      this.editingAlert = true
      this.handleDeliveryMethods()
    },
    confirmDeleteAlert(id: string): void {
      this.deleteViewOpen = true
      this.alertSelectedForDeletion = id
    },
    cancelDeleteAlert(): void {
      this.deleteViewOpen = false
      this.alertSelectedForDeletion = null
    },
    clearCurrentAlert(): void {
      this.currentAlert = structuredClone(toRaw(DEFAULT_ALERT))
    },
    toggleNotification(): void {
      this.isEnableNotification = !this.isEnableNotification
      this.isNotificationBtnEnabled = false
      this.isNotificationsTitle = false
      this.getPrimaryPhone()
    },
    toggleNotificationsTitle(): void {
      this.isNotificationsTitle = !this.isNotificationsTitle
    },
    async getPrimaryPhone(): Promise<void> {
      const resp = await this.userProfileStore.getPhones(APP_ENV.VITE_APP_ACCOUNTS_HOST)
      if (resp.status === HTTPStatus.OK) {
        const phones = resp.data as Phone[] | null
        this.phone = phones?.find((phone) => phone.isPrimary) ?? null
      } else {
        this.phone = null
      }
    },
    getFrequency(frequency: string): string {
      return this.$t(`map.deviceAlerts.fields.frequencyTypes.${frequency}`) as string
    }
  }
})
</script>

<style lang="scss">
.v-dialog.base-dialog {
  .device-alerts-dialog {
    .dialog-card--padded .scroll-wrapper__content > .v-card-text {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.restrictedContent {
  cursor: not-allowed;
  opacity: 0.36;
}

.emptyMessage {
  height: 240px;
  line-height: 240px;
}

.alerts-list {
  padding: 0;

  &-item {
    padding-block: 16px;

    &:first-child {
      padding-block-start: 0;
    }

    .v-list-item-title {
      font-size: 17px;
    }

    .v-list-item-subtitle {
      line-height: 1.2;
    }
  }

  &-item-actions {
    display: flex;
    align-items: center;
    padding: 8px 0;

    .v-btn {
      min-width: 120px;
      width: 120px;
    }
  }
}
</style>
